<div class="w-full">
  @if(loading){
  <div class="flex items-center justify-center py-6 text-sm text-gray-400">
    <svg class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
      </path>
    </svg>
    Loading tasks...
  </div>
  }

  <div cdkDropList (cdkDropListDropped)="onTaskDrop($event)" class="flex flex-col divide-y divide-gray-100">
    @for(task of tasks; track task.id){
    @let done = task.status === 'DONE';
    @let due = getDueInfo(task);
    @let priority = getPriorityInfo(task.priority);
    @let emps = getTaskEmployees(task);

    <div cdkDrag [cdkDragDisabled]="!canUserEditTask(task)" (click)="openTaskDetail(task.id!)"
      class="group relative flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer">

      <!-- Drag Handle (absolute, subtle) -->
      @if(canUserEditTask(task)) {
      <div cdkDragHandle (click)="$event.stopPropagation()"
        class="absolute left-0.5 top-1/2 -translate-y-1/2 cursor-move text-gray-300 hover:text-gray-500 transition-all opacity-0 group-hover:opacity-100">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
        </svg>
      </div>
      }

      <!-- Checkbox -->
      <button (click)="$event.stopPropagation(); toggleDone(task)"
        class="shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors cursor-pointer"
        [class.bg-emerald-500]="done" [class.border-emerald-500]="done"
        [class.border-gray-300]="!done" [class.hover:border-emerald-500]="!done" [class.bg-white]="!done"
        [attr.aria-label]="done ? 'Mark as not done' : 'Mark as done'">
        @if(done) {
        <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
        </svg>
        }
      </button>

      <!-- Task Name -->
      <span class="flex-1 min-w-0 text-sm font-medium truncate transition-colors"
        [class.text-gray-400]="done" [class.line-through]="done"
        [class.text-gray-900]="!done" [class.group-hover:text-blue-700]="!done">{{ task.nameTask }}</span>

      <!-- Links -->
      @if(task.links && task.links.length > 0) {
      <div class="flex items-center gap-1 shrink-0" (click)="$event.stopPropagation()">
        @for(link of task.links.slice(0, 2); track $index) {
        <a [href]="link.url" (click)="openExternalLink(link.url, $event)" target="_blank" rel="noopener noreferrer"
          [title]="link.name"
          class="inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer">
          @if(isDriveLink(link)) {
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 5.5L11.14 9zM9.4 15l-3.28 5.5h11.76L21.16 15zm12.02-1.5L15.28 3.5H8.42l6.14 10z" /></svg>
          } @else {
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m4.5-4.5l1.5-1.5a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656 0" /></svg>
          }
        </a>
        }
        @if(task.links.length > 2) {
        <span class="text-[10px] font-semibold text-gray-400">+{{ task.links.length - 2 }}</span>
        }
      </div>
      }

      <!-- Right metadata cluster -->
      <div class="flex items-center gap-4 shrink-0">

        <!-- Priority badge -->
        <div class="w-[4.5rem] flex justify-start">
          @if(priority && !done) {
          <span class="px-2.5 py-1 rounded-md text-xs font-semibold" [ngClass]="priority.classes">{{ priority.label }}</span>
          }
        </div>

        <!-- Due Date -->
        <span class="w-24 text-right text-xs whitespace-nowrap"
          [class.text-red-600]="due?.urgent" [class.font-semibold]="due?.urgent"
          [class.text-gray-500]="!due?.urgent && !done" [class.text-gray-400]="done && !due?.urgent">
          {{ due ? due.text : '—' }}
        </span>

        <!-- Hours -->
        <span class="w-12 text-right text-xs font-medium" [class.text-gray-400]="done" [class.text-gray-600]="!done">
          @if(task.estimatedHours) { {{ task.estimatedHours }}h } @else { <span class="text-gray-300 font-normal">—</span> }
        </span>

        <!-- Assignees -->
        <div class="w-16 flex items-center justify-end -space-x-1.5">
          @if(emps.length > 0) {
          @for(emp of emps.slice(0, 2); track emp.id) {
          <span class="inline-flex items-center justify-center w-6 h-6 rounded-full text-[9px] font-bold bg-indigo-100 text-indigo-700 border-2 border-white shadow-sm"
            [class.opacity-50]="done" title="{{ emp.firstName }} {{ emp.name }}">{{ getInitials(emp) }}</span>
          }
          @if(emps.length > 2) {
          <span class="inline-flex items-center justify-center w-6 h-6 rounded-full text-[9px] font-bold bg-gray-100 text-gray-500 border-2 border-white shadow-sm"
            title="{{ getEmployeeNames(emps) }}">+{{ emps.length - 2 }}</span>
          }
          } @else {
          <span class="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-dashed border-gray-200 text-gray-300">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
          </span>
          }
        </div>
      </div>
    </div>
    }
    @empty {
    @if(loadError) {
    <!-- A load failure must not read as "this phase is empty". -->
    <div class="flex items-start gap-3 m-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
      <svg class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <div class="min-w-0">
        <p class="text-sm font-semibold text-amber-800">Tasks unavailable</p>
        <p class="text-xs text-amber-700 mt-0.5">{{ loadError }}</p>
      </div>
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center py-10 text-gray-400">
      <span class="text-sm text-gray-500">No tasks yet</span>
      <span class="text-xs text-gray-400 mt-1">Add a task to start this phase</span>
    </div>
    }
    }
  </div>
</div>
